import { useEffect, useState } from "react";
import { SignInButton, SignUpButton } from "@clerk/clerk-react";
import { setPageMeta } from "../lib/pageMeta";

// Public marketing page served at casethread.app/ (and /welcome on any
// host) for visitors who haven't signed in yet. Sign-in / sign-up CTAs
// open Clerk's hosted modal — App.tsx's <SignedIn> gate then renders
// the authenticated app in place.
//
// Language rules (strict): not a legal product. Avoid "evidence",
// "legal letters", "build your case", "legally admissible".

interface Step {
  title: string;
  body: string;
  // Optional reassurance one-liner shown beneath the body. Used on
  // the first step to address the inevitable "wait, are you reading
  // my whole inbox?" question right where it surfaces.
  note?: string;
}

const STEPS: Step[] = [
  {
    title: "Forward your emails",
    body: "Send your correspondence to a unique case email address. CaseThread reads every message automatically.",
    note: "You control what's shared — only emails you forward are added to your case.",
  },
  {
    title: "AI builds your timeline",
    body: "Dates, key facts, parties, and commitments extracted instantly.",
  },
  {
    title: "Export your dossier",
    body: "Professional PDF ready for lawyers, regulators, or ombudsmen.",
  },
];

interface TrustPoint {
  emoji: string;
  title: string;
  body: string;
}

const TRUST: TrustPoint[] = [
  {
    emoji: "🔒",
    title: "You choose what to share",
    body: "Forward only the messages that matter to your case. Your data is encrypted, never shared, and only you can see it.",
  },
  {
    emoji: "🛡️",
    title: "Encrypted and isolated",
    body: "Every case is encrypted and isolated to your account. Hosted on Cloudflare infrastructure. AI processing by leading providers — or run it locally on your own machine.",
  },
  {
    emoji: "🗑️",
    title: "You're in control",
    body: "Export your data any time. Delete your account and every record with one click. No data retention after deletion.",
  },
];

const PERSONAL_FEATURES = [
  "Unlimited cases",
  "Email forwarding & screenshot capture",
  "Call recording and transcription",
  "AI interrogation and letter drafting",
  "PDF dossier export",
];

const SITUATIONS = [
  "Insurance claims",
  "Bank disputes",
  "Landlord issues",
  "Warranty complaints",
];

const AI_EXAMPLE = {
  prompt: "What has the supplier promised and when?",
  reply:
    "Based on your records, the supplier made three commitments: delivery by March 14 (email Mar 2), replacement parts by March 28 (call transcript Mar 15), and a full refund if unresolved by April 1 (email Mar 20). Only the replacement parts were delivered, two days late.",
};

// AI-drafted formal demand letter shown alongside the Timeline mockup
// in the hero. The content is derived from the Meridian / Invoice #0231
// timeline events so a reader can see the narrative arc at a glance:
// events on the left produced the draft on the right.
const AI_DRAFT = {
  to: "accounts@meridiansupply.com",
  subject: "Outstanding payment — Invoice #0231 (£2,450)",
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function LandingPage() {
  const [navScrolled, setNavScrolled] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // The marketing page is served at both casethread.app/ and /welcome
  // on any host. Both URLs canonicalize to https://casethread.app/ —
  // /welcome is just the friendly path used while signed-in users are
  // also at /. setPageMeta also clears any guide-specific JSON-LD that
  // may be lingering from a previous SPA navigation.
  useEffect(() => {
    setPageMeta({
      title: "CaseThread — Track every detail of your dispute or claim",
      description:
        "Forward your emails, let AI build your timeline, and export a professional dossier. Insurance claims, bank disputes, landlord issues — any case where proof matters.",
      canonical: "https://casethread.app/",
    });
  }, []);

  return (
    <div className="lp">
      {/* ---------- NAV ---------- */}
      <header className={`lp-nav${navScrolled ? " scrolled" : ""}`}>
        <div className="lp-container lp-nav-inner">
          <a className="lp-wordmark" href="/welcome">
            <img
              src="/casethread-icon.png?v=2"
              alt=""
              className="lp-wordmark-icon"
              width={28}
              height={28}
            />
            <span>CaseThread</span>
          </a>
          <button
            type="button"
            className="lp-nav-hamburger"
            aria-label={mobileNavOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileNavOpen}
            onClick={() => setMobileNavOpen((v) => !v)}
          >
            <span aria-hidden>{mobileNavOpen ? "✕" : "☰"}</span>
          </button>
          <nav
            className={`lp-nav-right${mobileNavOpen ? " mobile-open" : ""}`}
            aria-label="Primary"
          >
            <SignInButton mode="modal">
              <button type="button" className="lp-nav-link">
                Sign in
              </button>
            </SignInButton>
            <SignUpButton mode="modal">
              <button type="button" className="lp-btn lp-btn-primary">
                Start free
              </button>
            </SignUpButton>
          </nav>
        </div>
      </header>

      <main>
        {/* ---------- HERO ---------- */}
        <section className="lp-hero" id="about">
          <div className="lp-container lp-hero-inner">
            <div className="lp-hero-brand" aria-hidden>
              <img
                src="/casethread-icon.png?v=2"
                alt=""
                className="lp-hero-brand-icon"
                width={56}
                height={56}
              />
              <span className="lp-hero-brand-text">CaseThread</span>
            </div>
            <p className="lp-hero-tagline">Own the Narrative</p>
            <h1 className="lp-h1">
              Dealing with a bank, insurer, landlord, or company?
            </h1>
            <p className="lp-sub">
              Turn months of emails into a clear timeline, key facts, and
              a professional dossier — in minutes.
            </p>
            <ul className="lp-pills" aria-label="Common situations">
              {SITUATIONS.map((s) => (
                <li key={s} className="lp-pill">
                  {s}
                </li>
              ))}
            </ul>
            <div className="lp-cta-row">
              <SignUpButton mode="modal">
                <button
                  type="button"
                  className="lp-btn lp-btn-primary lp-btn-lg"
                >
                  Start free — 30 day trial
                </button>
              </SignUpButton>
              <SignInButton mode="modal">
                <button
                  type="button"
                  className="lp-btn lp-btn-ghost lp-btn-lg"
                >
                  Sign in
                </button>
              </SignInButton>
            </div>
            <p className="lp-no-card">No credit card required</p>
          </div>

          {/* Static HTML/CSS mockup of the Timeline visual view —
              fictional supplier dispute. Sits OUTSIDE .lp-hero-inner /
              .lp-container so the split (timeline + AI draft email)
              can use a wider max-width than the hero text. Class names
              are deliberately NOT shared with the real .timeline-visual
              styles in the authenticated app. */}
          <figure className="lp-screenshot">
              <div className="lp-mockup">
                <div className="lp-mockup-bar">
                  <div className="lp-mockup-bar-dots" aria-hidden>
                    <span className="lp-mockup-dot" />
                    <span className="lp-mockup-dot" />
                    <span className="lp-mockup-dot" />
                  </div>
                  <div className="lp-mockup-bar-case">
                    Meridian Supply Co.
                  </div>
                  <div className="lp-mockup-bar-tabs" aria-hidden>
                    <span className="lp-mockup-tab">Overview</span>
                    <span className="lp-mockup-tab">Records</span>
                    <span className="lp-mockup-tab active">Timeline</span>
                  </div>
                </div>
                <div className="lp-mockup-body lp-mockup-body-split">
                  <div className="lp-mockup-pane lp-mockup-pane-tl">
                    <ol className="lp-mock-tl" aria-hidden>
                      <li className="lp-mock-tl-event">
                        <span className="lp-mock-tl-dot" />
                        <div className="lp-mock-tl-card">
                          <div className="lp-mock-tl-date">
                            MAR 2, 2026
                          </div>
                          <div className="lp-mock-tl-title">
                            Invoice #0231 issued
                          </div>
                          <div className="lp-mock-tl-detail">
                            £2,450 sent to Meridian for delivered goods.
                          </div>
                          <div className="lp-mock-tl-foot">
                            <span className="lp-mock-tl-party">You</span>
                            <span className="lp-mock-tl-type">EMAIL</span>
                          </div>
                        </div>
                      </li>
                      <li className="lp-mock-tl-event">
                        <span className="lp-mock-tl-dot" />
                        <div className="lp-mock-tl-card">
                          <div className="lp-mock-tl-date">
                            MAR 28, 2026
                          </div>
                          <div className="lp-mock-tl-title">
                            Payment deadline missed
                          </div>
                          <div className="lp-mock-tl-detail">
                            No response to two follow-up emails.
                          </div>
                          <div className="lp-mock-tl-foot">
                            <span className="lp-mock-tl-party">You</span>
                            <span className="lp-mock-tl-type">EMAIL</span>
                          </div>
                        </div>
                      </li>
                      <li className="lp-mock-tl-event lp-mock-tl-key">
                        <span className="lp-mock-tl-dot" />
                        <div className="lp-mock-tl-card">
                          <div className="lp-mock-tl-date">
                            APR 5, 2026
                          </div>
                          <div className="lp-mock-tl-title">
                            Formal demand letter sent
                          </div>
                          <div className="lp-mock-tl-detail">
                            14-day deadline, citing all prior correspondence.
                          </div>
                          <div className="lp-mock-tl-foot">
                            <span className="lp-mock-tl-party">You</span>
                            <span className="lp-mock-tl-amount">
                              £2,450
                            </span>
                            <span className="lp-mock-tl-type">EMAIL</span>
                          </div>
                        </div>
                      </li>
                      <li className="lp-mock-tl-event lp-mock-tl-key">
                        <span className="lp-mock-tl-dot" />
                        <div className="lp-mock-tl-card">
                          <div className="lp-mock-tl-date">
                            APR 20, 2026
                          </div>
                          <div className="lp-mock-tl-title">
                            Payment received
                          </div>
                          <div className="lp-mock-tl-detail">
                            Bank transfer of £2,450 confirmed.
                          </div>
                          <div className="lp-mock-tl-foot">
                            <span className="lp-mock-tl-party">
                              Meridian Supply Co.
                            </span>
                            <span className="lp-mock-tl-amount">
                              £2,450
                            </span>
                            <span className="lp-mock-tl-type">EMAIL</span>
                          </div>
                        </div>
                      </li>
                    </ol>
                  </div>
                  <aside className="lp-mockup-pane lp-mock-draft" aria-hidden>
                    <div className="lp-mock-draft-head">
                      <span className="lp-mock-draft-badge">AI draft</span>
                      <span className="lp-mock-draft-sub">
                        Drafted from your timeline
                      </span>
                    </div>
                    <div className="lp-mock-draft-meta">
                      <div className="lp-mock-draft-row">
                        <span className="lp-mock-draft-label">To</span>
                        <span className="lp-mock-draft-val">
                          {AI_DRAFT.to}
                        </span>
                      </div>
                      <div className="lp-mock-draft-row">
                        <span className="lp-mock-draft-label">Subject</span>
                        <span className="lp-mock-draft-val lp-mock-draft-subject">
                          {AI_DRAFT.subject}
                        </span>
                      </div>
                    </div>
                    <div className="lp-mock-draft-body">
                      <p>Dear Meridian Supply Co.,</p>
                      <p>
                        I am writing regarding Invoice #0231 for{" "}
                        <strong>£2,450</strong>, issued 2 March 2026 and
                        due 28 March 2026. Payment remains outstanding
                        and two follow-up emails have gone unanswered.
                      </p>
                      <p>
                        Please settle the invoice in full within{" "}
                        <strong>14 days</strong>. If payment is not
                        received by 19 April 2026, I will pursue
                        recovery through the small claims court,
                        including interest and reasonable costs.
                      </p>
                      <p className="lp-mock-draft-sign">
                        Regards,
                        <br />
                        Your name
                      </p>
                    </div>
                    <div className="lp-mock-draft-actions">
                      <span className="lp-mock-draft-chip">
                        3 citations
                      </span>
                      <span className="lp-mock-draft-btn">
                        Copy to clipboard
                      </span>
                    </div>
                  </aside>
                </div>
              </div>
            </figure>
        </section>

        {/* ---------- HOW IT WORKS ---------- */}
        <section className="lp-section lp-how" id="how-it-works">
          <div className="lp-container">
            <h2 className="lp-h2">How it works</h2>
            <div className="lp-three-col">
              {STEPS.map((s, i) => (
                <article key={s.title} className="lp-how-card">
                  <div className="lp-how-num" aria-hidden>
                    {i + 1}
                  </div>
                  <h3 className="lp-card-title">{s.title}</h3>
                  <p className="lp-card-body">{s.body}</p>
                  {s.note && <p className="lp-how-note">{s.note}</p>}
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ---------- TRUST / PRIVACY ---------- */}
        <section className="lp-section lp-trust">
          <div className="lp-container">
            <h2 className="lp-h2">Your correspondence stays private</h2>
            <div className="lp-three-col">
              {TRUST.map((t) => (
                <article key={t.title} className="lp-how-card lp-trust-card">
                  <div className="lp-trust-icon" aria-hidden>
                    {t.emoji}
                  </div>
                  <h3 className="lp-card-title">{t.title}</h3>
                  <p className="lp-card-body">{t.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ---------- AI EXAMPLE ---------- */}
        <section className="lp-section lp-ai">
          <div className="lp-container">
            <h2 className="lp-h2">
              The AI reads everything. You ask the questions.
            </h2>
            <div className="lp-ai-mock">
              <div className="lp-ai-exchange">
                <div className="lp-ai-prompt">
                  <span className="lp-ai-role">You</span>
                  <div className="lp-ai-bubble lp-ai-bubble-user">
                    {AI_EXAMPLE.prompt}
                  </div>
                </div>
                <div className="lp-ai-reply">
                  <span className="lp-ai-role lp-ai-role-ai">
                    CaseThread
                  </span>
                  <div className="lp-ai-bubble lp-ai-bubble-ai">
                    {AI_EXAMPLE.reply}
                  </div>
                </div>
              </div>
            </div>
            <p className="lp-ai-note">
              Powered by leading AI models. Local AI option available —
              your data never leaves your device.
            </p>
          </div>
        </section>

        {/* ---------- GUIDES LINK ---------- */}
        <section className="lp-section lp-guides-link">
          <div className="lp-container">
            <a className="lp-guides-link-card" href="/guides">
              <div>
                <div className="lp-guides-link-eyebrow">Free resources</div>
                <div className="lp-guides-link-title">
                  Free guides on resolving disputes
                </div>
                <div className="lp-guides-link-sub">
                  Step-by-step playbooks for insurance appeals, deposit
                  disputes, bank complaints, CFPB filings, and more.
                </div>
              </div>
              <span className="lp-guides-link-arrow" aria-hidden>
                →
              </span>
            </a>
          </div>
        </section>

        {/* ---------- PRICING ---------- */}
        <section className="lp-section lp-pricing">
          <div className="lp-container">
            <h2 className="lp-h2">Simple pricing</h2>
            <div className="lp-plans lp-plans-single">
              <div className="lp-plan">
                <div className="lp-plan-name">Personal</div>
                <div className="lp-plan-trial">
                  Free for 30 days, no card required
                </div>
                <div className="lp-plan-price-big">
                  $9.99
                  <span className="lp-plan-period"> / month</span>
                </div>
                <div className="lp-plan-price-annual">
                  or <strong>$89.99/year</strong> (save 25%)
                </div>
                <ul className="lp-plan-features">
                  {PERSONAL_FEATURES.map((f) => (
                    <li key={f}>
                      <span className="lp-check" aria-hidden>
                        ✓
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>
                <SignUpButton mode="modal">
                  <button
                    type="button"
                    className="lp-btn lp-btn-primary lp-btn-lg lp-plan-cta"
                  >
                    Start free
                  </button>
                </SignUpButton>
              </div>
            </div>
          </div>
        </section>

        {/* ---------- ONE-LINE ASSURANCES ---------- */}
        <section className="lp-assurances">
          <div className="lp-container">
            <p className="lp-assurances-line">
              No ads. Your data stays yours. Delete any time.
            </p>
            <p className="lp-assurances-disclaimer">
              CaseThread is a correspondence tool, not a legal service.
              For legal advice, consult a qualified attorney or solicitor.
            </p>
          </div>
        </section>
      </main>

      {/* ---------- FOOTER ---------- */}
      <footer className="lp-footer">
        <div className="lp-container lp-footer-inner">
          <div className="lp-footer-brand">
            <span className="lp-wordmark-small">CaseThread</span>
            <span className="lp-footer-copy">© 2026 CaseThread</span>
          </div>
          <nav className="lp-footer-links" aria-label="Footer">
            <a href="#about">About</a>
            <span aria-hidden>·</span>
            <a href="/guides">Guides</a>
            <span aria-hidden>·</span>
            <a href="/privacy">Privacy</a>
            <span aria-hidden>·</span>
            <a href="/terms">Terms</a>
            <span aria-hidden>·</span>
            <a href="/changelog">Changelog</a>
            <span aria-hidden>·</span>
            <a href="mailto:hello@casethread.app">Contact</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
